import React, { useCallback, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Select } from '../../../components/common/Select.js';
import type { MenuStaticActionConfig } from '../../../components/common/SelectMenuStaticAction.tsx';
import type { OptionLabel } from '../../../types/options.ts';
import { Tooltip } from '../../../admin/components/shared/Tooltip.tsx';
import { truncateProcessDisplayName } from '../utils/riskDetailsUtils.ts';
import { PaginatedSelectMenuList } from './PaginatedSelectMenuList.tsx';
import { TENANT_PATHS } from '../../../routing/paths.ts';

const CONTROL_SELECT_STYLES = {
  placeholder: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
    ...base,
    fontWeight: 600,
    color: state.isFocused ? '#6b7280' : '#111827',
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 70,
  }),
  control: (base: Record<string, unknown>) => ({
    ...base,
    width: '100%',
    minHeight: '2rem',
    borderColor: '#93c5fd',
    boxShadow: 'none',
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    width: '100%',
    marginTop: 4,
    border: '1px solid #e5e7eb',
    borderRadius: '0.375rem',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
    zIndex: 70,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    maxHeight: 280,
    overflowY: 'auto',
  }),
  option: (base: Record<string, unknown>, state: { isSelected?: boolean }) => ({
    ...base,
    whiteSpace: 'normal',
    textAlign: 'left',
    ...(state.isSelected && {
      backgroundColor: 'var(--primary-color-light, #2563eb)',
      color: '#fff',
    }),
  }),
};

interface RiskControlAssociationsProps {
  selectedValues: string[];
  controlOptions: OptionLabel[];
  selectedLabelsByValue?: Record<string, string>;
  fixedTagContainer?: boolean;
  tagLayout?: 'pills' | 'rows';
  /** Controls copy/behavior for control pickers vs process pickers on control view. */
  variant?: 'control' | 'process';
  addOptionLabel?: string;
  sectionTitle?: string;
  sectionHint?: string;
  sectionTitleClassName?: string;
  loadingOptions?: boolean;
  loadingMoreOptions?: boolean;
  searchInputValue?: string;
  hasMoreOptions?: boolean;
  onSearchChange?: (term: string) => void;
  onLoadMore?: () => void;
  readOnly?: boolean;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  /** Optional actions rendered next to the section title (before the add button). */
  titleActions?: React.ReactNode;
}

function resolveControlLabel(
  id: string,
  controlOptions: OptionLabel[],
  selectedLabelsByValue: Record<string, string>,
  options: { readOnly?: boolean; payloadLabelsOnly?: boolean } = {}
): string {
  if (selectedLabelsByValue[id]) return selectedLabelsByValue[id];
  if (options.readOnly || options.payloadLabelsOnly) return id;
  return controlOptions.find((o) => o.value === id)?.label ?? id;
}

export const RiskControlAssociations: React.FC<RiskControlAssociationsProps> = ({
  selectedValues,
  controlOptions,
  selectedLabelsByValue = {},
  fixedTagContainer = false,
  tagLayout = 'pills',
  variant = 'control',
  addOptionLabel,
  sectionTitle = 'Controls',
  sectionHint = 'Mitigating controls linked to this risk.',
  sectionTitleClassName = 'text-xs font-semibold text-slate-700',
  loadingOptions = false,
  loadingMoreOptions = false,
  searchInputValue = '',
  hasMoreOptions = false,
  onSearchChange,
  onLoadMore,
  readOnly = false,
  onAdd,
  onRemove,
  titleActions = null,
}) => {
  const isProcessVariant = variant === 'process';
  const entitySingular = isProcessVariant ? 'process' : 'control';
  const entityPlural = isProcessVariant ? 'processes' : 'controls';
  const payloadLabelsOnly = isProcessVariant && readOnly;
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdding, setIsAdding] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const remainingOptions = useMemo(
    () => controlOptions.filter((opt) => !selectedSet.has(opt.value)),
    [controlOptions, selectedSet]
  );

  const selectOptions = useMemo(
    () => remainingOptions.map((option) => ({ value: option.value, label: option.label })),
    [remainingOptions]
  );
  const noOptionsMessage = useMemo(
    () => () =>
      loadingOptions ? `Loading ${entityPlural}...` : `No ${entityPlural} found`,
    [entityPlural, loadingOptions]
  );
  const placeholder = remainingOptions.length
    ? addOptionLabel ?? `Select ${entitySingular}...`
    : `No more ${entityPlural} available`;

  const controlSelectProps = useMemo(
    () => ({
      loadingMoreOptions,
      hasMoreOptions,
      onOptionsNearEnd: () => {
        if (hasMoreOptions && !loadingMoreOptions) onLoadMore?.();
      },
    }),
    [hasMoreOptions, loadingMoreOptions, onLoadMore]
  );

  const closePicker = () => {
    setIsAdding(false);
    onSearchChange?.('');
  };
  const navigateToAddControl = useCallback(() => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    navigate(TENANT_PATHS.SOX_CONTROLS_NEW, { state: { returnTo } });
  }, [location.hash, location.pathname, location.search, navigate]);

  const menuStaticAction = useMemo<MenuStaticActionConfig | undefined>(
    () =>
      isProcessVariant
        ? undefined
        : {
            label: 'Add new control',
            onClick: navigateToAddControl,
            icon: <Plus className="h-4 w-4 shrink-0" aria-hidden />,
            'aria-label': 'Add new control',
          },
    [isProcessVariant, navigateToAddControl]
  );

  const controlSelectComponents = useMemo(
    () => ({
      MenuList: PaginatedSelectMenuList,
    }),
    []
  );

  const tagsWrapperClass =
    tagLayout === 'pills'
      ? fixedTagContainer
        ? 'flex max-h-40 min-h-40 flex-wrap content-start gap-2 overflow-y-auto p-0.5'
        : 'flex flex-wrap gap-2'
      : fixedTagContainer
        ? 'flex max-h-40 min-h-[2.5rem] flex-col gap-1 overflow-y-auto py-0.5'
        : 'flex max-h-48 flex-col gap-1 overflow-y-auto py-0.5';

  return (
    <div className="space-y-2 w-full min-w-0">
        <div className="flex justify-between items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <p className={sectionTitleClassName}>{sectionTitle}</p>
              {titleActions}
            </div>
            <div className="w-full border-t border-slate-300 mt-1" />
          </div>
          {readOnly ? (
            <Tooltip content={`Click edit to add ${entityPlural}`} side="left">
              <span className="inline-flex shrink-0">
                <button
                  type="button"
                  disabled
                  className="flex h-6 w-6 shrink-0 cursor-not-allowed items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-400 opacity-80 shadow-none"
                  aria-label={`Add ${entitySingular} (disabled in view mode)`}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </span>
            </Tooltip>
          ) : !isAdding ? (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
              aria-label={`Add ${entitySingular}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      {sectionHint ? <p className="text-xs text-slate-500">{sectionHint}</p> : null}
      {!readOnly && isAdding ? (
        <div className="w-full min-w-0">
          <Select
            aria-label={placeholder}
            value={null}
            placeholder={placeholder}
            onChange={(value: string | string[]) => {
              if (value && typeof value === "string") onAdd(value);
              closePicker();
            }}
            isSearchable
            inputValue={searchInputValue}
            onInputChange={(value: string, meta: { action: string }) => {
              if (meta.action === 'input-change') onSearchChange?.(value);
            }}
            menuIsOpen={isAdding}
            onMenuClose={() => {
              onSearchChange?.('');
              setIsAdding(false);
            }}
            onMenuScrollToBottom={() => {
              if (hasMoreOptions && !loadingMoreOptions) onLoadMore?.();
            }}
            noOptionsMessage={noOptionsMessage}
            menuStaticAction={menuStaticAction}
            components={controlSelectComponents}
            {...controlSelectProps}
            styles={CONTROL_SELECT_STYLES}
            options={selectOptions}
            menuPlacement="auto"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="absolute"
            className="w-full max-w-full block"
            autoFocus
            defaultMenuIsOpen
          />
        </div>
      ) : null}
      {tagLayout === 'pills' ? (
        <div className={tagsWrapperClass}>
          {selectedValues.map((id) => {
            const label = resolveControlLabel(id, controlOptions, selectedLabelsByValue, {
              readOnly,
              payloadLabelsOnly,
            });
            const labelDisplay = truncateProcessDisplayName(label, 40);
            return (
              <Tooltip key={id} content={label}>
                <span className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md border border-indigo-200/90 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-900">
                  <span className="truncate">{labelDisplay}</span>
                  {!readOnly ? (
                    <button
                      type="button"
                      onClick={() => onRemove(id)}
                      className="shrink-0 rounded-sm p-0.5 text-indigo-700 hover:bg-indigo-100/80"
                      aria-label={`Remove ${label}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </span>
              </Tooltip>
            );
          })}
        </div>
      ) : selectedValues.length > 0 ? (
        <div className={tagsWrapperClass} role="list">
          {selectedValues.map((id) => {
            const label = resolveControlLabel(id, controlOptions, selectedLabelsByValue, {
              readOnly,
              payloadLabelsOnly,
            });
            const labelDisplay = truncateProcessDisplayName(label, 40);
            return (
              <div key={id} className="flex items-center justify-between gap-2 min-w-0 py-0.5" role="listitem">
                <Tooltip content={label}>
                  <span className="text-xs text-slate-800 truncate min-w-0">{labelDisplay}</span>
                </Tooltip>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => onRemove(id)}
                    className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
